import { useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHeader, DataTable, StatusBadge, Sel, BranchSel, Field, opts } from "@/components/kit";
import { api, useApi, fmtErr, dt, label, fileToDataUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const EXC_TYPES = ["lost_ticket", "plate_mismatch", "vehicle_not_recognized", "duplicate_entry", "unpaid_vehicle", "barrier_failure", "payment_failure", "rfid_failure", "network_outage", "lost_card", "wrong_plate", "system_error", "manual_override", "customer_dispute"];

function NewExc({ onClose, onSaved }) {
  const { branches, branch } = useAuth();
  const [f, setF] = useState({ type: "lost_ticket", branch_id: branch || branches[0]?.id, plate: "", description: "", photo: null });
  const save = async () => {
    try {
      const { data } = await api.post("/exceptions", f);
      toast.success(`Case ${data.case_no} opened`);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-card" data-testid="exception-new-dialog">
        <DialogHeader><DialogTitle>New exception case</DialogTitle></DialogHeader>
        <Field label="Type"><Sel value={f.type} onChange={(v) => setF({ ...f, type: v })} options={opts(EXC_TYPES)} testid="exception-type" className="w-full" /></Field>
        <Field label="Branch"><BranchSel value={f.branch_id} onChange={(v) => setF({ ...f, branch_id: v })} all={false} testid="exception-branch" className="w-full" /></Field>
        <Field label="Plate"><Input data-testid="exception-plate" className="font-mono uppercase" value={f.plate} onChange={(e) => setF({ ...f, plate: e.target.value.toUpperCase() })} /></Field>
        <Field label="Description"><Textarea data-testid="exception-description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
        <Field label="Photo"><Input type="file" accept="image/*" data-testid="exception-photo" onChange={async (e) => e.target.files[0] && setF({ ...f, photo: await fileToDataUrl(e.target.files[0]) })} /></Field>
        <Button data-testid="exception-save" disabled={!f.description} onClick={save}>Open case</Button>
      </DialogContent>
    </Dialog>
  );
}

function ExcDetail({ id, onClose, onSaved }) {
  const { can } = useAuth();
  const { data, reload } = useApi(id ? `/exceptions/${id}` : null, {});
  const [f, setF] = useState({ status: "under_review", resolution: "" });
  const save = async () => {
    try {
      await api.put(`/exceptions/${id}`, f);
      toast.success("Case updated");
      reload();
      onSaved();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };
  return (
    <Dialog open={!!id} onOpenChange={onClose}>
      <DialogContent className="max-w-xl bg-card" data-testid="exception-detail-dialog">
        <DialogHeader><DialogTitle className="font-mono">{data?.case_no}</DialogTitle></DialogHeader>
        {data && (
          <div className="space-y-3 text-sm">
            <div className="flex flex-wrap gap-2"><StatusBadge status={data.type} tone="blue" /><StatusBadge status={data.status} testid="exception-status" />{data.plate && <span className="plate text-xs">{data.plate}</span>}</div>
            <p>{data.description}</p>
            {data.photo && <img src={data.photo} alt="evidence" className="max-h-48 rounded border" />}
            {data.resolution && <p className="rounded bg-emerald-500/10 p-2 text-emerald-500">Resolution: {data.resolution} — {data.approver}</p>}
            <div className="max-h-40 space-y-1 overflow-y-auto border-t pt-2">
              {data.history?.map((h, i) => <div key={i} className="text-xs"><span className="font-mono text-muted-foreground">{dt(h.at)}</span> • <b>{h.by}</b> {label(h.action)} {h.note && `— ${h.note}`}</div>)}
            </div>
            {can("edit", "approve") && data.status !== "resolved" && (
              <div className="grid gap-2 border-t pt-3">
                <Sel value={f.status} onChange={(v) => setF({ ...f, status: v })} options={opts(["open", "under_review", "resolved", "escalated"])} testid="exception-new-status" className="w-full" />
                <Textarea data-testid="exception-resolution" placeholder="Resolution / note" value={f.resolution} onChange={(e) => setF({ ...f, resolution: e.target.value })} />
                <Button data-testid="exception-update" onClick={save}>Update case</Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Exceptions() {
  const { branch, setBranch, can, branchName } = useAuth();
  const [f, setF] = useState({ status: "", type: "", q: "" });
  const [add, setAdd] = useState(false);
  const [sel, setSel] = useState(null);
  const { data, loading, reload } = useApi("/exceptions", { ...f, branch_id: branch });
  return (
    <div className="fade-up">
      <PageHeader title="Exception Management" subtitle="Each exception creates a case number with a full resolution trail" testid="exceptions-header">
        <Input data-testid="exceptions-search" className="w-40" placeholder="Case / plate" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} />
        <Sel value={f.type} onChange={(v) => setF({ ...f, type: v })} testid="exceptions-type-filter" allLabel="All types" options={opts(EXC_TYPES)} />
        <Sel value={f.status} onChange={(v) => setF({ ...f, status: v })} testid="exceptions-status-filter" allLabel="All statuses" options={opts(["open", "under_review", "resolved", "escalated"])} />
        <BranchSel value={branch} onChange={setBranch} testid="exceptions-branch" />
        {can("add") && <Button data-testid="exception-add-btn" onClick={() => setAdd(true)}><Plus className="h-4 w-4" /> Case</Button>}
      </PageHeader>
      <DataTable testid="exceptions-table" loading={loading} rows={data} onRowClick={(r) => setSel(r.id)} columns={[
        { key: "case_no", label: "Case", render: (r) => <span className="font-mono text-xs font-bold">{r.case_no}</span> },
        { key: "created_at", label: "Date/time", render: (r) => dt(r.created_at) },
        { key: "branch", label: "Branch", render: (r) => branchName(r.branch_id) },
        { key: "type", label: "Type", render: (r) => label(r.type) },
        { key: "plate", label: "Vehicle", render: (r) => (r.plate ? <span className="plate text-xs">{r.plate}</span> : "—") },
        { key: "description", label: "Description", render: (r) => <span className="block max-w-xs truncate text-xs">{r.description}</span> },
        { key: "created_by", label: "User" },
        { key: "approver", label: "Approver" },
        { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
      ]} />
      {add && <NewExc onClose={() => setAdd(false)} onSaved={reload} />}
      <ExcDetail id={sel} onClose={() => setSel(null)} onSaved={reload} />
    </div>
  );
}
